import {
  // eslint-disable-next-line no-unused-vars
  h,
  Component,
  render,
  createRef,
} from 'preact';
import axios, { CancelToken } from 'axios';
import { TweenLite } from 'gsap';

import ResultList from './search/ResultList';

class SearchModule extends Component {
  state = {
    query: '',
    filter: [],
    businessFilter: [],
    page: 1,
    maxPages: 1,
    entries: [],
    count: [],
    loading: false,
    filterLoading: false,
    paginating: false,
    jetpack: document.body.classList.contains('jetpack-is-active'),
  };

  constructor(props) {
    super(props);

    this.cancelToken = () => {};
    this.inputRef = createRef();

    document.documentElement.addEventListener('keydown', event => {
      const { keyCode } = event;

      if (keyCode === 27) {
        this.closeModal();
      }
    });
  }

  handleInputChange = event => {
    this.setState(
      {
        page: 1,
        query: event.target.value,
        filter: [],
        businessFilter: [],
        maxPages: 1,
        loading: true,
        entries: [],
        count: [],
      },
      this.runSearchQuery,
    );
  };

  runSearchQuery = async () => {
    const { query, page, filter, businessFilter, jetpack, count } = this.state;

    if (!query) {
      return;
    }

    try {
      this.cancelToken();

      let filterQuery = '';
      if (filter.length > 0) {
        filterQuery = `&subtype=${filter}`;
      }

      if (jetpack) {
        // Use Jetpack
        let businessFilterQuery = '';
        if (businessFilter.length > 0) {
          businessFilterQuery = `&business_cats=${businessFilter}`;
        }

        const searchResponse = await axios.get(
          `/wp-json/deepsea/v1/search?page=${page}&per_page=10&s=${query}${filterQuery}${businessFilterQuery}`,
          {
            cancelToken: new CancelToken(c => {
              this.cancelToken = c;
            }),
          },
        );

        if (!Array.isArray(searchResponse.data.posts) || !searchResponse.data.posts.length) {
          this.setState({
            entries: false,
            maxPages: 0,
            loading: false,
            filterLoading: false,
            paginating: false,
          });
          return;
        }

        const types = searchResponse.data.post_types;
        delete types.attachment;
        delete types.wp_block;
        delete types.nav_menu_item;
        delete types.wp_navigation;
        delete types.wp_template;
        delete types.wp_template_part;
        // Deepsea Post Types
        delete types['newsroom-news'];
        // Jetpack Post Types
        delete types.feedback;
        delete types.jp_pay_product;
        delete types.jp_pay_order;

        const businesses = searchResponse.data.business_cats;

        this.setState({
          entries: searchResponse.data.posts,
          maxPages: searchResponse.data.total_pages,
          loading: false,
          filterLoading: false,
          paginating: false,
          types,
          businesses,
          count: filterQuery === '' ? searchResponse.data.post_types_count : count,
        });
      } else {
        // Use WP REST API
        const searchResponse = await axios.get(
          `/wp-json/wp/v2/search?page=${page}&per_page=5&search=${query}${filterQuery}&_embed`,
          {
            cancelToken: new CancelToken(c => {
              this.cancelToken = c;
            }),
          },
        );

        const typeResponse = await axios.get('/wp-json/wp/v2/types', {
          cancelToken: new CancelToken(c => {
            this.cancelToken = c;
          }),
        });

        const types = typeResponse;
        delete types.data.attachment;
        delete types.data.wp_block;

        this.setState({
          entries: searchResponse.data,
          maxPages: searchResponse.headers['x-wp-totalpages'],
          loading: false,
          filterLoading: false,
          paginating: false,
          types,
        });
      }
    } catch (e) {
      if (!axios.isCancel(e)) {
        this.setState({
          loading: false,
          filterLoading: false,
          paginating: false,
        });
      }
    }
  };

  setFilter = newFilter => event => {
    const { filter } = this.state;

    if (event.target.checked) {
      filter.push(newFilter);
    } else {
      const index = filter.indexOf(newFilter);
      if (index > -1) {
        filter.splice(index, 1);
      }
    }

    this.setState(
      {
        filter,
        filterLoading: true,
        page: 1,
      },
      this.runSearchQuery,
    );
  };

  setBusiness = newBusinessFilter => event => {
    const { businessFilter } = this.state;

    if (event.target.checked) {
      businessFilter.push(newBusinessFilter);
    } else {
      const index = businessFilter.indexOf(newBusinessFilter);
      if (index > -1) {
        businessFilter.splice(index, 1);
      }
    }

    this.setState(
      {
        businessFilter,
        filterLoading: true,
        page: 1,
      },
      this.runSearchQuery,
    );
  };

  loadPage = props => {
    const { loading, paginating } = this.state;

    if (paginating || loading) {
      return;
    }

    // Scroll to top of search modal
    const searchModalTop = document.getElementById('search-modal-top');
    TweenLite.to(searchModalTop, 0.6, { scrollTo: { y: 0, offsetY: 0 } });

    this.setState(
      {
        page: parseInt(props.target.innerText, 10),
        paginating: true,
      },
      this.runSearchQuery,
    );
  };

  closeModal = () => {
    document.documentElement.classList.remove('search-modal-active');
    this.inputRef.current.blur();
  };

  openModal = () => {
    document.documentElement.classList.add('search-modal-active');
    setTimeout(() => this.inputRef.current.focus(), 100);
  };

  handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) {
      this.closeModal();
    }
  };

  handleCrossKeydown = ({ keyCode }) => {
    if (keyCode === 13) {
      this.closeModal();
    }
  };

  PageNumbers = () => {
    const { maxPages, page } = this.state;
    const pages = [];
    for (let i = 1; i <= maxPages; i += 1) {
      if (i === page) {
        pages.push(<span className="page-numbers current">{i}</span>);
      } else if (
        (i < page + 3 && i > page) ||
        (i > page - 3 && i < page) ||
        i === 1 ||
        i === maxPages
      ) {
        pages.push(
          <button onClick={this.loadPage} className="page-numbers" tabIndex="0" type="button">
            {i}
          </button>,
        );
      } else if (i === page + 3 || i === page - 3) {
        pages.push(
          <button className="page-numbers" tabIndex="0" type="button" disabled>
            ...
          </button>,
        );
      }
    }

    return <div className="pagination">{pages}</div>;
  };

  render() {
    const {
      filter,
      query,
      entries,
      page,
      paginating,
      maxPages,
      loading,
      filterLoading,
      types,
      jetpack,
      count,
      businesses,
      businessFilter,
    } = this.state;
    const { PageNumbers } = this;

    return (
      <div className="search-modal-wrapper active">
        <div className="search-modal">
          <div className="search-modal-header">
            <div className="search-modal-header-inner">
              <span className="header-logo" />
              <div className="modal-header--form deepsea-form">
                <div className="input-css-search">
                  <label aria-label="Search">
                    <input
                      id="input-focus"
                      ref={this.inputRef}
                      type="text"
                      onInput={this.handleInputChange}
                      value={query}
                      placeholder="What are you looking for?"
                      onKeyDown={this.handleKeyDown}
                    />
                  </label>
                  <i className="search-submit"></i>
                </div>
              </div>
              <div className="primaryNav-search">
                <button
                  type="button"
                  className="primaryNav-search-button"
                  onClick={this.closeModal}
                  onKeyDown={this.handleCrossKeydown}
                >
                  <i className="icon-x" />
                  Close
                </button>
              </div>
            </div>
          </div>
          <div id="search-modal-top" className="search-modal-results">
            <section className="section">
              <div className="section-wrapper">
                {!loading && entries.length !== 0 && query.length !== 0 && jetpack && (
                  <div className="wp-block-deepsea-columns has-2-columns wp-block-columns">
                    <div className="wp-block-deepsea-column col-desk-4 col-tab-4 col-mob-12">
                      <h4>Filter By Type</h4>
                      <div className="search-modal-results-filters deepsea-form">
                        {types &&
                          Object.keys(types).map(key => (
                            <label className="label-checkbox" htmlFor={`post-type-${key}`}>
                              <input
                                checked={filter.includes(key)}
                                onClick={this.setFilter(key)}
                                type="checkbox"
                                id={`post-type-${key}`}
                                name="post-type"
                                value={key}
                                disabled={
                                  !count[key] || (key !== 'post' && businessFilter.length !== 0)
                                }
                              />
                              <span
                                className={
                                  (!count[key] ||
                                    (key !== 'post' && businessFilter.length !== 0)) &&
                                  'label-checkbox--disabled'
                                }
                              >
                                <p>
                                  {types[key].label === 'Posts' ? 'Insights' : types[key].label}
                                  {count[key] ? ` (${count[key]})` : ' (0)'}
                                </p>
                              </span>
                            </label>
                          ))}
                      </div>
                      <h4>Filter By Business</h4>
                      <div className="search-modal-results-filters deepsea-form">
                        {businesses &&
                          Object.keys(businesses).map(key => (
                            <label
                              className="label-checkbox"
                              htmlFor={`business-cat-${businesses[key].slug}`}
                            >
                              <input
                                checked={businessFilter.includes(businesses[key].slug)}
                                onClick={this.setBusiness(businesses[key].slug)}
                                type="checkbox"
                                id={`business-cat-${businesses[key].slug}`}
                                name="business-cat"
                                value={businesses[key].slug}
                                disabled={filter.length !== 0 && !filter.includes('post')}
                              />
                              <span
                                className={
                                  filter.length !== 0 &&
                                  !filter.includes('post') &&
                                  'label-checkbox--disabled'
                                }
                              >
                                <p>{businesses[key].name}</p>
                              </span>
                            </label>
                          ))}
                      </div>
                    </div>
                    <div className="wp-block-deepsea-column col-desk-8 col-tab-8 col-mob-12">
                      {!loading && entries.length !== 0 && (
                        <h3>{`Search results for '${query}'`}</h3>
                      )}
                      {!filterLoading && !paginating && entries !== false && (
                        <div className="post-list">
                          <ResultList entries={entries} jetpack={jetpack} types={types.data} />
                        </div>
                      )}
                      {maxPages >= page &&
                        maxPages > 1 &&
                        !filterLoading &&
                        !paginating &&
                        entries !== false && <div className="grid-footer">{PageNumbers()}</div>}
                      {(filterLoading || paginating) && (
                        <p className="deepsea-loader deepsea-loader--small">
                          <span className="deepsea-spinner">Loading...</span>
                          Please wait
                        </p>
                      )}
                      {!filterLoading && !paginating && entries === false && (
                        <p>No results found</p>
                      )}
                    </div>
                  </div>
                )}
                {!loading && entries.length !== 0 && query.length !== 0 && !jetpack && (
                  <div>
                    {!loading && entries.length !== 0 && <h3>{`Search results for '${query}'`}</h3>}
                    {!filterLoading && !paginating && entries !== false && (
                      <div className="post-list">
                        <ResultList entries={entries} jetpack={jetpack} types={types.data} />
                      </div>
                    )}
                    {maxPages >= page &&
                      maxPages > 1 &&
                      !filterLoading &&
                      !paginating &&
                      entries !== false && <div className="grid-footer">{PageNumbers()}</div>}
                    {(filterLoading || paginating) && (
                      <p className="deepsea-loader deepsea-loader--small">
                        <span className="deepsea-spinner">Loading...</span>
                        Please wait
                      </p>
                    )}
                    {!filterLoading && !paginating && entries === false && <p>No results found</p>}
                  </div>
                )}
                {loading && !paginating && entries.length === 0 && query.length !== 0 && (
                  <div className="deepsea-loader-wrap">
                    <p className="deepsea-loader">
                      <span className="deepsea-spinner">Loading...</span>
                      Please wait
                    </p>
                  </div>
                )}
                {!loading && !paginating && entries.length === 0 && query.length !== 0 && (
                  <h2>No results found</h2>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }
}

const setup = () => {
  const $ele = document.getElementById('search-module');
  if ($ele) {
    render(<SearchModule />, $ele);
  }
};
setup();
